import { useQuery } from "@tanstack/react-query";
import { Loader2, RotateCcw } from "lucide-react";
import React from "react";

import {
  type TransferOrderChange,
  getTransferOrderChanges,
} from "@/api/transfer_check";
import { Button } from "@/components/ui/button";
import { extractErrorMessage } from "@/lib/api-errors";
import { Dialog } from "@/lib/scan-ui/overlays";
import { fmtQty } from "@/lib/scan-ui/status";

const KIND_TEXT: Record<TransferOrderChange["kind"], string> = {
  increase: "больше",
  decrease: "меньше",
  add: "новая строка",
  remove: "уберётся",
};

const KIND_CLS: Record<TransferOrderChange["kind"], string> = {
  increase: "text-amber-700",
  decrease: "text-amber-700",
  add: "text-sky-700",
  remove: "text-red-700",
};

/** Пока предпросмотра нет, «Завершить» неактивна — подтверждать нечего. */
const NO_CONFIRM = (): void => undefined;

function q(qty: number, unit: string): string {
  return unit ? `${fmtQty(qty)} ${unit}` : fmtQty(qty);
}

/**
 * Подтверждение «Завершить»: перед созданием перемещения показывает, как
 * портал перепишет заказ под факт (`GET …/order-changes` — тот же расчёт, что
 * и само завершение). Пока предпросмотр не получен или бэкенд отказал (409 —
 * правка невозможна, например строка под резервом), завершать нельзя: человек
 * должен видеть, что произойдёт с заказом, а отказ — его словами.
 *
 * «Завершить» уносит `plan_token` показанного предпросмотра: если собранное
 * успело измениться, бэкенд отвечает 409, а родитель передаёт `staleError` и
 * перечитывает предпросмотр — диалог показывает новый список с причиной.
 */
export function FinalizeDialog({
  sessionId,
  busy,
  staleError,
  onConfirm,
  onCancel,
}: {
  sessionId: string;
  busy: boolean;
  /** Отказ «собранное изменилось после просмотра» — показать над списком. */
  staleError?: string | null;
  onConfirm: (planToken: string) => void;
  onCancel: () => void;
}): React.ReactElement {
  const changesQ = useQuery({
    queryKey: ["tcv2-order-changes", sessionId],
    queryFn: () => getTransferOrderChanges(sessionId),
    // Предпросмотр — на момент открытия диалога: факт мог измениться.
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const docNote = "В 1С будет создан документ «Перемещение запасов» (без проведения).";

  if (changesQ.isPending) {
    return (
      <Dialog
        title="Завершить проверку?"
        body={
          <div className="flex items-center gap-2 py-2 text-slate-500">
            <Loader2 className="size-4 animate-spin" /> Считаем, как изменится заказ…
          </div>
        }
        confirmLabel="Завершить"
        confirmDisabled
        onConfirm={NO_CONFIRM}
        onCancel={onCancel}
      />
    );
  }

  if (changesQ.isError) {
    return (
      <Dialog
        title="Завершить проверку?"
        body={
          <div className="space-y-2">
            <div
              role="alert"
              className="whitespace-pre-line rounded-lg border border-red-200 bg-red-50 p-2 text-[12px] font-semibold text-red-700"
            >
              {extractErrorMessage(changesQ.error, "Не удалось рассчитать изменения заказа")}
            </div>
            <Button
              variant="outline"
              className="h-10 w-full"
              disabled={changesQ.isFetching}
              onClick={() => void changesQ.refetch()}
            >
              {changesQ.isFetching ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <RotateCcw className="size-4" />
              )}{" "}
              Повторить
            </Button>
          </div>
        }
        confirmLabel="Завершить"
        confirmDisabled
        onConfirm={NO_CONFIRM}
        onCancel={onCancel}
      />
    );
  }

  const { order_number, changes, plan_token } = changesQ.data;
  const confirm = () => onConfirm(plan_token);
  // Перечитываем после 409 — подтверждать старый список нельзя.
  const refreshing = changesQ.isFetching;
  const stale = staleError ? (
    <div
      role="alert"
      className="mb-2 whitespace-pre-line rounded-lg border border-amber-300 bg-amber-50 p-2 text-[12px] font-semibold text-amber-800"
    >
      {staleError}
      {refreshing && (
        <Loader2 className="ml-1 inline size-3.5 animate-spin" aria-label="Обновляем" />
      )}
    </div>
  ) : null;
  if (changes.length === 0) {
    return (
      <Dialog
        title="Завершить проверку?"
        body={
          <>
            {stale}
            {docNote}
          </>
        }
        confirmLabel="Завершить"
        confirmBusy={busy}
        confirmDisabled={refreshing}
        onConfirm={confirm}
        onCancel={onCancel}
      />
    );
  }
  return (
    <Dialog
      title={`Заказ ${order_number} будет изменён под факт`}
      body={
        <div className="space-y-2">
          {stale}
          <ul className="max-h-[50dvh] space-y-1 overflow-y-auto">
            {changes.map((c) => (
              <li
                key={`${c.nomenclature_ref}|${c.characteristic_ref}`}
                data-testid="order-change"
                className="rounded-md border bg-slate-50 px-2 py-1.5"
              >
                <div className="text-[12px] font-semibold text-slate-800">
                  {c.nomenclature_name}
                </div>
                {c.characteristic_name && (
                  <div className="text-[11px] text-slate-500">{c.characteristic_name}</div>
                )}
                <div className="flex items-center justify-between text-[12px]">
                  <span className="font-mono tabular-nums">
                    {q(c.qty_before, c.base_unit_name)} → {q(c.qty_after, c.base_unit_name)}
                  </span>
                  <span className={`text-[11px] font-bold ${KIND_CLS[c.kind]}`}>
                    {KIND_TEXT[c.kind]}
                  </span>
                </div>
              </li>
            ))}
          </ul>
          <div>Затем {docNote.charAt(0).toLowerCase() + docNote.slice(1)}</div>
        </div>
      }
      confirmLabel="Изменить заказ и завершить"
      confirmBusy={busy}
      confirmDisabled={refreshing}
      onConfirm={confirm}
      onCancel={onCancel}
    />
  );
}
